import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export interface SnackbarMessage {
  text: string;
  action?: { label: string; onClick: () => void };
  duration?: number;
}

type Show = (m: SnackbarMessage) => void;

const SnackbarContext = createContext<Show>(() => {});

/** Provides a single M3 snackbar queue to the app. */
export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<(SnackbarMessage & { id: number })[]>([]);
  const nextId = useRef(0);
  const show = useCallback<Show>((m) => setQueue((q) => [...q, { ...m, id: nextId.current++ }]), []);
  const current = queue[0];
  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  useEffect(() => {
    if (!current) return undefined;
    const t = setTimeout(dismiss, current.duration ?? 4000);
    return () => clearTimeout(t);
  }, [current, dismiss]);

  const value = useMemo(() => show, [show]);
  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <div className="snackbar-host" role="status" aria-live="polite">
        {current && (
          <div className="snackbar" key={current.id}>
            <span>{current.text}</span>
            {current.action && (
              <button
                type="button"
                className="snackbar__action"
                onClick={() => {
                  current.action?.onClick();
                  dismiss();
                }}
              >
                {current.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar(): Show {
  return useContext(SnackbarContext);
}
